import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import * as d3 from "d3";

import { alignDatasetsStart, buildChartAreas, extent, getValue } from "../LineChartUtils";
import XAxis from "./XAxis";
import YAxis from "./YAxis";
import Line from "./Line";
import InteractiveArea from "./InteractiveArea";
import { PointCollision } from "./collision/PointCollision";
import BandCollision from "./collision/BandCollision";
import IChartCollision from "./collision/IChartCollision";

interface LineChartAxis {
  dataKey: string;
  valueGetter?(value: any, row: DatasetRecord, array: Array<DatasetRecord>): any;
  valueFormatter?(value: any): number;
}

export interface DatasetRecord {
  [key: string]: any;
  renderDot?(props: {
    color: string | undefined,
    index: number
    isHovered: boolean,
    record: DatasetRecord,
    x: number,
    y: number,
  }): ReactNode;
}

export interface LineChartDataset {
  color?: string;
  records: Array<DatasetRecord>;
  xAxis: LineChartAxis;
  yAxis: LineChartAxis;
}

export type AxisBaseline = "truncated" | "zero";
export type TickInterval = "firstLast" | "interval";
export type SelectionType = "band" | "point";

interface LineChartProps {
  alignStart?: boolean;
  datasets: Array<LineChartDataset>;
  fontSize?: number;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  }
  onClick?(record: Array<DatasetRecord>): void;
  onHover?(record: DatasetRecord | null): void;
  renderTooltip?(records: Array<DatasetRecord>): ReactNode;
  selectionType?: SelectionType;
  showDots?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  xTickFormatter?(value: number, index: number, domain: Array<number>): string;
  xTickInterval?: TickInterval;
  yBaseline?: AxisBaseline;
  yTickFormatter?(value: number, index: number, domain: Array<number>): string;
  yTickInterval?: TickInterval;
}

export default function LineChart(props: LineChartProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [hoveredRecords, setHoveredRecords] = useState<Array<DatasetRecord>>([]);
  const [clickRecord, setClickRecord] = useState<DatasetRecord | null>(null);

  const alignStart = props.alignStart ?? false;
  const selectionType = props.selectionType ?? "band";
  const fontSize = props.fontSize ?? 14;
  const showDots = props.showDots ?? true;
  const showXAxis = props.showXAxis ?? true;
  const showYAxis = props.showYAxis ?? true;
  const yBaseline = props.yBaseline ?? "zero";
  const margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...props.margin
  };
  const tickLength = fontSize / 2;

  // Format and cache data
  for (const dataset of props.datasets) {
    for (const record of dataset.records) {
      record["_x"] = getValue(record, dataset.records, dataset.xAxis);
      record["_y"] = getValue(record, dataset.records, dataset.yAxis);
    }
  }

  if (alignStart) {  
    alignDatasetsStart(props.datasets);
  }

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setWidth(width);
        setHeight(height);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const { lineArea, xAxis, yAxis } = buildChartAreas(
    { fontSize, height, margin, showXAxis, showYAxis, tickLength, width }
  );

  const xExtent = extent(props.datasets, (d) => d._x ?? 0);
  const xScale = d3.scaleLinear(xExtent, [0, lineArea.width]);
  const yExtent = extent(props.datasets, (d) => d._y ?? 0).reverse();
  if (yBaseline === "zero") {
    yExtent[1] = 0;
  }
  const yScale = d3.scaleLinear(yExtent, [0, lineArea.height]);

  let collision: IChartCollision;
  if (selectionType === "point") {
    collision = new PointCollision(props.datasets, 24, xScale, yScale);
  } else {
    collision = new BandCollision(props.datasets, xScale);
  }

  const canRender = lineArea.height > 0 && lineArea.width > 0 && xAxis.height >= 0 && xAxis.width >= 0 && yAxis.height >= 0 && yAxis.width >= 0;

  return (
    <Tooltip 
      open={!!hoveredRecords.length && !!props.renderTooltip}
      title={!!hoveredRecords.length && !!props.renderTooltip && props.renderTooltip(hoveredRecords)}
      followCursor
    >
      <Box ref={containerRef} sx={{ height: "100%" }}>
        {containerRef.current && canRender &&
          <svg 
            height={height}
            width={width}
            viewBox={`0 0 ${width} ${height}`}
          >
            {showXAxis &&
              <XAxis
                bounds={xAxis}
                fontSize={fontSize}
                scale={xScale}
                tickFormatter={(value, index, domain) => {
                  if (props.xTickFormatter) {
                    return props.xTickFormatter(value, index, domain);
                  }
                  return `${value}`;
                }}
                tickInterval={props.xTickInterval ?? "firstLast"}
              />
            }

            {showYAxis &&
              <YAxis
                bounds={yAxis}
                fontSize={fontSize}
                scale={yScale}
                tickFormatter={(value, index, domain) => {
                  if (props.yTickFormatter) {
                    return props.yTickFormatter(value, index, domain);
                  }
                  return `${value}`;
                }}
                tickInterval={props.yTickInterval ?? "firstLast"}
              />
            }

            {props.datasets.map((dataset, index) => {
              return (
                <Line
                  key={`line-${index}`}
                  bounds={lineArea}
                  color={dataset.color}
                  dataset={dataset}
                  hoveredRecords={hoveredRecords}
                  showDots={showDots}
                  xScale={xScale}
                  yScale={yScale}
                />
              )
            })}

            <InteractiveArea
              bounds={lineArea}
              onMouseDown={(event) => {
                const found = collision.getCollision(event)[0];
                setClickRecord(found);
              }}
              onMouseLeave={() => {
                setHoveredRecords([]);
                setClickRecord(null);
              }}
              onMouseMove={(event) => {
                const found = collision.getCollision(event);
                setHoveredRecords(found);
              }}
              onMouseUp={(event) => {
                const records = collision.getCollision(event);
                const found = records[0];
                if (clickRecord && found === clickRecord) {
                  props.onClick?.(records);
                }
                setClickRecord(null);
              }}
            />
          </svg>
        }
      </Box>
    </Tooltip>
  );
}